import { useEffect, useId, useRef, useState, type SetStateAction } from 'react'
import { ChevronLeft, ChevronRight, ListChecks, Search, SlidersHorizontal, X } from 'lucide-react'
import type { TransactionFilters as ApiFilters } from '@/api/transactions'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import type { FilterMode } from '@/hooks/useTransactionFilters'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Select } from '@/components/ui/select'
import { DateRangeFilterFields } from '@/components/transactions/TransactionDateRangeFilter'
import { currentMonth, useDateRangeFilter } from '@/lib/transactionDateRangeFilter'
import {
  SEARCH_MAX,
  SEARCH_MIN,
  type TransactionLinkFilters,
  type TransactionType,
} from '@/lib/transactionsLink'
import { cn } from '@/lib/utils'
import { PILLS, filterChips, type FilterChip, type FilterOptions } from './filterChips'

function shiftMonth(month: string, by: number): string {
  const [y, m] = month.split('-').map(Number)
  const d = new Date(y, m - 1 + by, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function monthShort(month: string): string {
  const [y, m] = month.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
}

function SearchBox({
  value,
  onSearch,
}: {
  value?: string
  onSearch: (q: string | undefined) => void
}) {
  const [text, setText] = useState(value ?? '')
  const debounced = useDebouncedValue(text, 350)
  const applied = useRef(value)
  useEffect(() => {
    const trimmed = debounced.trim()
    const next = trimmed.length >= SEARCH_MIN ? trimmed.slice(0, SEARCH_MAX) : undefined
    if (next === applied.current) return
    applied.current = next
    onSearch(next)
  }, [debounced, onSearch])

  return (
    <label className="flex h-10 min-w-0 flex-1 items-center gap-2 rounded-[10px] border border-[var(--fl-border)] bg-[var(--fl-bg)] px-2.5 text-[var(--fl-text-3)] focus-within:border-[var(--fl-primary)]">
      <Search size={16} aria-hidden="true" className="shrink-0" />
      <input
        type="search"
        value={text}
        maxLength={SEARCH_MAX}
        onChange={(e) => setText(e.target.value)}
        placeholder="Search descriptions"
        aria-label="Search descriptions"
        className="min-w-0 flex-1 bg-transparent text-sm text-[var(--fl-text-1)] outline-none placeholder:text-[var(--fl-text-3)]"
      />
    </label>
  )
}

function MonthStepper({ month, onChange }: { month: string; onChange: (m: string) => void }) {
  return (
    <div className="flex h-10 shrink-0 items-center rounded-[10px] border border-[var(--fl-border)]">
      <button
        type="button"
        aria-label="Previous month"
        onClick={() => onChange(shiftMonth(month, -1))}
        className="grid h-10 w-9 place-items-center rounded-l-[10px] text-[var(--fl-text-2)] hover:bg-[var(--fl-surface-hover)]"
      >
        <ChevronLeft size={16} />
      </button>
      <span className="whitespace-nowrap px-1 text-[13.5px] font-semibold text-[var(--fl-text-1)]">
        {monthShort(month)}
      </span>
      <button
        type="button"
        aria-label="Next month"
        onClick={() => onChange(shiftMonth(month, 1))}
        className="grid h-10 w-9 place-items-center rounded-r-[10px] text-[var(--fl-text-2)] hover:bg-[var(--fl-surface-hover)]"
      >
        <ChevronRight size={16} />
      </button>
    </div>
  )
}

function Chips({
  chips,
  onRemove,
  onClearAll,
}: {
  chips: FilterChip[]
  onRemove: (patch: Partial<TransactionLinkFilters>) => void
  onClearAll: () => void
}) {
  if (chips.length === 0) return null
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          aria-label={`Remove filter ${chip.label}`}
          onClick={() => onRemove(chip.remove)}
          className="flex h-[30px] items-center gap-1.5 rounded-full bg-[var(--fl-primary-subtle)] pl-2.5 pr-2 text-[12.5px] font-medium text-[var(--fl-primary-text)] hover:brightness-95"
        >
          {chip.label}
          <X size={12} aria-hidden="true" />
        </button>
      ))}
      <button
        type="button"
        onClick={onClearAll}
        className="h-[30px] px-1.5 text-[12.5px] font-medium text-[var(--fl-text-2)] underline underline-offset-2 hover:text-[var(--fl-text-1)]"
      >
        Clear all
      </button>
    </div>
  )
}

/** Every field. Used inline on desktop (applies live) and inside the phone sheet (on a draft). */
function FilterFields({
  value,
  onChange,
  options,
}: {
  value: TransactionLinkFilters
  onChange: (next: TransactionLinkFilters) => void
  options: FilterOptions
}) {
  // The date-range control drives a setState-style setter; adapt it onto this panel's value.
  const asApi = value as ApiFilters
  const setFromAction = (action: SetStateAction<ApiFilters>) => {
    const next = { ...(typeof action === 'function' ? action(asApi) : action) }
    delete next.cursor
    delete next.limit
    onChange(next as TransactionLinkFilters)
  }
  const dateRange = useDateRangeFilter(asApi, setFromAction)
  const set = (patch: Partial<TransactionLinkFilters>) => onChange({ ...value, ...patch })
  const field = 'flex flex-col gap-1 text-xs font-medium text-[var(--fl-text-2)]'

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <DateRangeFilterFields
          filters={value}
          activePreset={dateRange.activePreset}
          customOpen={dateRange.customOpen}
          customMode={dateRange.customMode}
          onSelectPreset={dateRange.selectDatePreset}
          onSelectCustomMode={dateRange.selectCustomMode}
          updateFilter={(key, v) => set({ [key]: v || undefined })}
        />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div className={field}>
          Type
          <Select
            aria-label="Type"
            value={value.type ?? ''}
            onChange={(v) => set({ type: (v || undefined) as TransactionType | undefined })}
          >
            <option value="">All types</option>
            <option value="EXPENSE">Spent</option>
            <option value="INCOME">Income</option>
            <option value="TRANSFER">Transfers</option>
            <option value="REFUND">Refunds</option>
            <option value="PERSON_PAYMENT">People payments</option>
          </Select>
        </div>
        <div className={field}>
          Account
          <Select
            aria-label="Account"
            value={value.financialAccountId ?? ''}
            onChange={(v) => set({ financialAccountId: v || undefined })}
          >
            <option value="">All accounts</option>
            {options.accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.label}
              </option>
            ))}
          </Select>
        </div>
        <div className={field}>
          Category
          <Select
            aria-label="Category"
            searchable
            value={value.categoryId ?? ''}
            onChange={(v) => set({ categoryId: v || undefined })}
          >
            <option value="">All categories</option>
            {options.categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </Select>
        </div>
        <div className={field}>
          Member
          <Select
            aria-label="Member"
            value={value.attributedToHouseholdMemberId ?? ''}
            onChange={(v) => set({ attributedToHouseholdMemberId: v || undefined })}
          >
            <option value="">Everyone</option>
            {options.members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </Select>
        </div>
        <div className={field}>
          Person
          <Select
            aria-label="Person"
            searchable
            value={value.contactId ?? ''}
            onChange={(v) => set({ contactId: v || undefined })}
          >
            <option value="">Anyone</option>
            {options.contacts
              .filter((c) => !c.archived || c.id === value.contactId)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
          </Select>
        </div>
        <div className={field}>
          Goal
          <Select
            aria-label="Goal"
            value={value.goalId ?? ''}
            onChange={(v) => set({ goalId: v || undefined })}
          >
            <option value="">Any goal</option>
            {options.goals.map((g) => (
              <option key={g.id} value={g.id}>
                {g.label}
              </option>
            ))}
          </Select>
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm text-[var(--fl-text-1)]">
        <Checkbox
          checked={!!value.isCapitalEvent}
          onChange={(e) => set({ isCapitalEvent: e.target.checked || undefined })}
        />
        One-off (capital) events only
      </label>
      <label className="flex items-center gap-2 text-sm text-[var(--fl-text-1)]">
        <Checkbox
          checked={value.reconciled === false}
          onChange={(e) => set({ reconciled: e.target.checked ? false : undefined })}
        />
        Not reconciled only
      </label>
    </div>
  )
}

function FiltersSheet({
  initial,
  options,
  onApply,
  onClose,
}: {
  initial: TransactionLinkFilters
  options: FilterOptions
  onApply: (next: TransactionLinkFilters) => void
  onClose: () => void
}) {
  const [draft, setDraft] = useState(initial)
  const titleId = useId()
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose()
        }}
        className="flex max-h-[88vh] w-full flex-col rounded-t-[20px] bg-[var(--fl-surface)] shadow-[rgba(0,0,0,0.15)_0px_8px_40px] motion-safe:animate-fade-slide-up"
      >
        <div
          aria-hidden="true"
          className="mx-auto mt-2 h-1 w-10 rounded-full bg-[var(--fl-border)]"
        />
        <div className="flex items-center justify-between px-4 pt-2">
          <h2 id={titleId} className="font-serif text-xl font-semibold text-[var(--fl-heading)]">
            Filters
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="grid h-11 w-11 place-items-center rounded-lg text-[var(--fl-text-2)] hover:bg-[var(--fl-surface-hover)]"
          >
            <X size={20} />
          </button>
        </div>
        <div className="overflow-y-auto px-4 py-3">
          <FilterFields value={draft} onChange={setDraft} options={options} />
        </div>
        <div className="flex gap-3 border-t border-[var(--fl-divider)] px-4 pt-3 pb-[max(16px,env(safe-area-inset-bottom))]">
          <Button
            variant="outline"
            className="h-11 flex-1"
            onClick={() => setDraft({ month: currentMonth(), q: draft.q })}
          >
            Clear
          </Button>
          <Button className="h-11 flex-1" onClick={() => onApply(draft)}>
            Apply
          </Button>
        </div>
      </div>
    </div>
  )
}

const DESKTOP_QUERY = '(min-width: 768px)'

export function TransactionFiltersBar({
  filters,
  options,
  mode,
  onModeChange,
  onChange,
  onUpdate,
  onClearAll,
  onSelect,
}: {
  filters: TransactionLinkFilters
  options: FilterOptions
  mode: FilterMode
  onModeChange: (mode: FilterMode) => void
  onChange: (next: TransactionLinkFilters) => void
  onUpdate: (patch: Partial<TransactionLinkFilters>) => void
  onClearAll: () => void
  /** Starts select mode from the pill row (phones, where the list's own button is hidden). */
  onSelect?: () => void
}) {
  const [sheetOpen, setSheetOpen] = useState(false)
  // Remounts the search box so Clear all empties the text too.
  const [searchKey, setSearchKey] = useState(0)
  const chips = filterChips(filters, options)
  const full = mode === 'full'

  function clearAll() {
    setSearchKey((k) => k + 1)
    onClearAll()
  }

  function openFull() {
    if (window.matchMedia?.(DESKTOP_QUERY).matches) onModeChange('full')
    else setSheetOpen(true)
  }

  return (
    <div className="flex flex-col gap-2.5 md:rounded-2xl md:border md:border-[var(--fl-border)] md:bg-[var(--fl-surface)] md:p-3 md:shadow-[var(--card-shadow-sm)]">
      <div className="flex items-center gap-2">
        <SearchBox key={searchKey} value={filters.q} onSearch={(q) => onUpdate({ q })} />
        {filters.month && (
          <div className={cn(full && 'md:hidden')}>
            <MonthStepper month={filters.month} onChange={(month) => onUpdate({ month })} />
          </div>
        )}
        {full && (
          <Button
            variant="outline"
            onClick={() => onModeChange('short')}
            aria-label="Show fewer filters"
            className="hidden h-10 shrink-0 gap-1.5 rounded-full px-3 text-[13px] md:flex"
          >
            <SlidersHorizontal size={14} aria-hidden="true" /> Short
          </Button>
        )}
      </div>

      {/* A saved Full choice applies on wide screens; a phone keeps the short bar and its sheet. */}
      {full && (
        <div className="hidden md:block">
          <FilterFields value={filters} onChange={onChange} options={options} />
        </div>
      )}

      <div className={cn('flex items-center gap-1.5', full && 'md:hidden')}>
        <div className="flex min-w-0 gap-1.5 overflow-x-auto [scrollbar-width:none] md:flex-wrap">
          {PILLS.map(([label, type]) => {
            const active = filters.type === type
            return (
              <button
                key={label}
                type="button"
                aria-pressed={active}
                onClick={() => onUpdate({ type })}
                className={cn(
                  'h-8 shrink-0 rounded-full border px-3 text-[13px] font-medium transition-colors',
                  active
                    ? 'border-[var(--fl-text-1)] bg-[var(--fl-text-1)] text-[var(--fl-surface)]'
                    : 'border-[var(--fl-border)] text-[var(--fl-text-2)] hover:bg-[var(--fl-surface-hover)]'
                )}
              >
                {label}
              </button>
            )
          })}
          {onSelect && (
            <button
              type="button"
              onClick={onSelect}
              className="flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-[var(--fl-border)] px-3 text-[13px] font-medium text-[var(--fl-primary)] hover:bg-[var(--fl-surface-hover)]"
            >
              <ListChecks size={14} aria-hidden="true" /> Select
            </button>
          )}
        </div>
        <button
          type="button"
          aria-label="Show all filters"
          onClick={openFull}
          className="ml-auto flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-[var(--fl-border)] px-2.5 text-[13px] font-medium text-[var(--fl-text-2)] hover:bg-[var(--fl-surface-hover)]"
        >
          <SlidersHorizontal size={14} aria-hidden="true" /> Full
        </button>
      </div>

      <Chips chips={chips} onRemove={onUpdate} onClearAll={clearAll} />

      {sheetOpen && (
        <FiltersSheet
          initial={filters}
          options={options}
          onClose={() => setSheetOpen(false)}
          onApply={(next) => {
            onChange(next)
            setSheetOpen(false)
          }}
        />
      )}
    </div>
  )
}
